import React from 'react';
import { wealthAssistant } from '../data/index';

interface WealthAssistantFeature {
    icon: string;
    label: string;
}

interface WealthAssistant {
    icon: string;
    eyebrow: string;
    title: string;
    subtitle: string;
    description: string[];
    features: WealthAssistantFeature[];
    ctaLabel: string;
    ctaHref: string;
}

const assistant = wealthAssistant as WealthAssistant;

/**
 * Eye-catching promotional banner for the Forge Ultra Wealth-Building Assistant.
 * Mirrors the Market Radar banner's dark gradient language, with a Key Features
 * grid and an animated gradient CTA that opens the membership page in a new tab.
 */
const WealthAssistantBanner: React.FC = () => {
    return (
        <section aria-labelledby="wealth-assistant-heading" className="w-full">
            <div
                className="relative overflow-hidden rounded-3xl
                           bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950
                           border border-emerald-400/30
                           shadow-2xl shadow-emerald-500/10
                           px-6 py-10 sm:px-10 sm:py-12 lg:px-14 lg:py-16"
            >
                {/* Decorative glow accents */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-28 -left-20 h-80 w-80 rounded-full bg-emerald-500/20 blur-[110px]"
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-28 -right-20 h-80 w-80 rounded-full bg-indigo-500/25 blur-[110px]"
                />
                {/* Faint dot matrix */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 bg-[radial-gradient(rgba(148,163,184,0.12)_1px,transparent_1px)] bg-[size:22px_22px] [mask-image:radial-gradient(ellipse_70%_70%_at_70%_30%,#000_40%,transparent_100%)]"
                />

                <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-14 items-start">
                    {/* Copy column */}
                    <div className="max-w-3xl">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-400/15 border border-emerald-400/30 text-emerald-300">
                                <i className={`${assistant.icon} text-2xl`} aria-hidden="true"></i>
                            </span>
                            <span className="inline-flex items-center gap-2 px-3 py-1 text-[10px] sm:text-xs font-bold tracking-widest text-emerald-300 uppercase bg-emerald-400/10 rounded-full border border-emerald-400/20">
                                <span className="relative flex h-2 w-2">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75 motion-reduce:animate-none" />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                                </span>
                                {assistant.eyebrow}
                            </span>
                        </div>

                        <h2
                            id="wealth-assistant-heading"
                            className="mt-5 text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white leading-tight"
                        >
                            {assistant.title}
                        </h2>

                        <p className="mt-3 text-base sm:text-lg font-semibold text-emerald-300 leading-snug">
                            {assistant.subtitle}
                        </p>

                        <div className="mt-5 space-y-3">
                            {assistant.description.map((paragraph) => (
                                <p key={paragraph.slice(0, 32)} className="text-sm sm:text-[15px] text-slate-300 leading-relaxed">
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    </div>

                    {/* Key Features + CTA column */}
                    <div className="flex flex-col gap-6">
                        <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-5 sm:p-6">
                            <h3 className="text-xs sm:text-sm font-bold uppercase tracking-widest text-slate-200">
                                Key Features
                            </h3>
                            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {assistant.features.map((feature) => (
                                    <li
                                        key={feature.label}
                                        className="group flex items-start gap-3 rounded-xl border border-emerald-400/15 bg-slate-900/60 p-3.5
                                                   transition-colors duration-300 hover:border-emerald-400/40 hover:bg-slate-900/90"
                                    >
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-400/25 to-green-500/10 border border-emerald-400/30 text-emerald-300">
                                            <i className={`${feature.icon} text-lg`} aria-hidden="true"></i>
                                        </span>
                                        <span className="text-sm font-semibold leading-snug text-slate-100">
                                            {feature.label}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <a
                            href={assistant.ctaHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative inline-flex w-full items-center justify-center gap-2.5
                                       overflow-hidden rounded-2xl px-7 py-4 sm:py-[18px]
                                       bg-gradient-to-r from-green-600 via-emerald-500 to-teal-500 bg-[length:200%_100%] bg-left
                                       text-white text-base sm:text-lg font-bold tracking-tight
                                       ring-1 ring-inset ring-white/20
                                       shadow-lg shadow-emerald-500/30
                                       hover:bg-right hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-emerald-500/50
                                       active:translate-y-0
                                       focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900
                                       transition-all duration-500"
                        >
                            {/* moving shine highlight */}
                            <span
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0 -translate-x-full
                                           bg-gradient-to-r from-transparent via-white/35 to-transparent
                                           transition-transform duration-700 ease-out
                                           group-hover:translate-x-full"
                            />
                            <i className="bx bx-line-chart relative text-xl" aria-hidden="true"></i>
                            <span className="relative">{assistant.ctaLabel}</span>
                            <i className="bx bx-right-arrow-alt relative text-2xl transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true"></i>
                            <span className="sr-only">(opens in a new tab)</span>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WealthAssistantBanner;
